import {computed, ComputedRef, effectScope, Ref, ref, ToRef, watch} from "vue";
import {autobind} from "@/decorators/autobind";
import ViewController from "@/interfaces/ViewController";
// @ts-ignore
import AppController from '@target/components/App/ts/AppController';
import PaymentService, {createPaymentPayload} from "@/services/operations/payment/PaymentService";
import LogService from "@/services/log-service/LogService";
import PurchaseRepository from "@/repositories/purchase/PurchaseRepository";
import AgentsAmountRanges from "@models/errors/components/AgentsAmountRanges";
import ModelAgent from "@models/agents/ModelAgent";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import {DocumentTypes} from "@/services/firebase/firestore/documents/DocumentTypes";
import DocumentPurchase from "@/services/firebase/firestore/documents/DocumentPurchase";
import ErrorsService from "@/services/errors-service/ErrorsService";
import ServiceAccount from "@/services/v2/data/service-account/ServiceAccount";
import ModelAccount from "@models/v2/account/ModelAccount";
import ModelAccountCurrency from "@models/v2/account/ModelAccountCurrency";
import ServiceExtWFlow from "@/services/v2/service-ext-w-flow/ServiceExtWFlow";
import {ServiceWarningBlocks} from "@/services/v2/service-warning-blocks/ServiceWarningBlocks";
import ModelWarningBlockNotification from "@models/v2/firebase/warning-blocks/ModelWarningBlockNotification";
import ModelAccountBalanceCurrency from "@models/v2/account/ModelAccountBalanceCurrency";
import RouterService from "@/services/RouterService";
import ServiceAgents from "@/services/v2/data/service-agents/ServiceAgents";

export enum CreatePaymentFlow {
  classic = 'classic',
  fromBromoney = 'from_bromoney'
}

export default class NewPaymentController implements ViewController {
  private scope = effectScope(true)
  private static instance?: NewPaymentController;
  // private readonly _selectedPaymentSystem: Ref<any> = ref(null);
  private readonly _currency: ComputedRef<ModelAccountCurrency | ModelAccountBalanceCurrency>;
  private _flow = ref<CreatePaymentFlow>(CreatePaymentFlow.classic);
  private _isCanSelectAgent = ref<boolean>(Boolean(import.meta.env.VITE_PURCHASE_IS_CAN_SELECT_AGENT));
  private _minAmountEnabled = ref<boolean>(Boolean(import.meta.env.VITE_PURCHASE_MIN_AMOUNT_ENABLED));
  private _minAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_MIN_AMOUNT));
  private _defaultAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_DEFAULT_AMOUNT));
  private _defaultExtWFlowAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_DEFAULT_EXT_W_FLOW_AMOUNT));
  private _agent: Ref<ModelAgent | null> = ref(null);
  private _purchaseRepository = new PurchaseRepository();
  private _initialized: boolean = false;
  private _account: Ref<ModelAccount>;

  private _paymentService: PaymentService;
  private _serviceExtWFlow: ServiceExtWFlow;
  private _routerService: RouterService;
  private _serviceAccount: ServiceAccount;
  private _serviceAgents: ServiceAgents;
  private _serviceWarningBlocks: ServiceWarningBlocks;

  private _warningBlocks: ComputedRef<ModelWarningBlockNotification[]>;
  private _warningClassicBlocks: ComputedRef<ModelWarningBlockNotification[]>;
  private _warningForBromoneyBlocks: ComputedRef<ModelWarningBlockNotification[]>;

  public agentsAmountRanges = ref<AgentsAmountRanges | null>(null);
  public isAgentsRangesLoading = ref<boolean>(false);
  public amount: Ref<number>;
  public minMaxRangesAmountByFlow: ComputedRef<{ min: number, max: number}>

  public isUpdatingValueInput = ref(false);
  public inputAmountFocused = ref(false);
  public textMaxMinError = ref(false);
  public isSubmitLoading = ref(false);

  private constructor() {
    this._serviceWarningBlocks = ServiceWarningBlocks.of();
    this._warningBlocks = this.scope.run(() => computed(() =>
      this._serviceWarningBlocks.warningBlocks.value?.paymentCreateView
        ?.filter(n => n.isActive)
        .sort((a, b) => a.order! - b.order!) ?? []
    )) as ComputedRef;
    this._warningClassicBlocks = this.scope.run(() =>
      computed(() => this._warningBlocks.value.filter(n => n.tag === 'classic') ?? [])
    ) as ComputedRef;
    this._warningForBromoneyBlocks = this.scope.run(() =>
      computed(() => this._warningBlocks.value.filter(n => n.tag === 'from_bromoney') ?? [])
    ) as ComputedRef;
    this._paymentService = AppController.getInstance().paymentService;
    this._routerService = AppController.getInstance().routerService;

    this._currency = this.scope.run(() =>
      computed(() => {
        if (this._flow.value === CreatePaymentFlow.fromBromoney && this.canUseExtWFlow()) {
          return this._account.value.wallet!.balance.currency!;
        }

        return this._account.value.currency;
      })
    ) as ComputedRef;
    // this._selectedPaymentSystem = ref(this.initSelectedPaymentSystem());
    // this._selectedPaymentSystem.value.currencies[0].dSums = this.calculateStartDefaultAmounts();
    this._serviceExtWFlow = AppController.of().service("extWControl") as ServiceExtWFlow;

    this._serviceAccount = ServiceAccount.of();
    this._account = this._serviceAccount.account as Ref<ModelAccount>;

    this.amount = ref(this.calculateStartAmountValue());

    this.minMaxRangesAmountByFlow = this.scope.run(() => computed(() => {
      if (this.flow.value === CreatePaymentFlow.classic) {
        if (this.agentsAmountRanges.value === null) return { min: 0, max: 0 };
        const {min, max} = this.agentsAmountRanges.value;

        if (!min || !max) return { min: 0, max: 0 };

        return { min: Math.ceil(min), max: Math.floor(max) };
      } else {
        if (this.canUseExtWFlow()) {
          return {
            min: this._account.value.wallet?.balance.amount !== undefined ? 2 : 0,
            max: this._account.value.wallet?.balance.amount || 0
          }
        } else {
          return { min: 0, max: 0 }
        }
      }
    })) as ComputedRef;

    this._serviceAgents = ServiceAgents.of();
    this._agent = this._serviceAgents.selectedPurchaseAgent;
  }

  static getInstance() {
    if (typeof this.instance === 'undefined') {
      this.instance = new NewPaymentController();
    }
    return this.instance;
  }

  get account(): Ref<ModelAccount> {
    return this._account;
  }

  get flow(): ToRef<CreatePaymentFlow> {
    return this._flow;
  }

  set flow(value: ToRef<CreatePaymentFlow>) {
    this._flow = value;
  }

  get agent() {
    return this._agent;
  }

  get paymentService(): PaymentService {
    return this._paymentService;
  }

  get routerService(): RouterService {
    return this._routerService;
  }

  get isCanSelectAgent(): ToRef<boolean> {
    return this._isCanSelectAgent;
  }

  get warningClassicBlocks(): ComputedRef<ModelWarningBlockNotification[]> {
    return this._warningClassicBlocks;
  }

  get warningForBromoneyBlocks(): ComputedRef<ModelWarningBlockNotification[]> {
    return this._warningForBromoneyBlocks;
  }

  get currency(): Ref<ModelAccountCurrency | ModelAccountBalanceCurrency> {
    return this._currency;
  }

  // get selectedPaymentSystem(): Ref<any> {
  //   return this._selectedPaymentSystem;
  // }

  public async init() {
    if (this._initialized) return;
    this.setPurchaseFirebaseConfig();
    this._initialized = true;
  }

  public canUseExtWFlow() {
    const allowTypes = ['dev-integration', 'integration'];
    if (allowTypes.includes(import.meta.env.VITE_APP_TYPE)) {
      if (!this._serviceExtWFlow || !this._account.value) {
        return false;
      }
      return this._serviceExtWFlow.canUseExtWFlow(this._account.value);
    } else {
      return false;
    }
  }

  private setPurchaseFirebaseConfig() {
    const doc = FirebaseService.of().firestoreService?.getDocument<DocumentPurchase>(DocumentTypes.PURCHASE);

    if (doc) {
      if (doc.isCanSelectAgent) {
        this._isCanSelectAgent.value = doc.isCanSelectAgent!;
      }

      if (doc.minAmountEnabled) {
        this._minAmountEnabled.value = doc.minAmountEnabled!;
      }

      if (doc.defaultAmount !== null) {
        this._defaultAmount.value = doc.defaultAmount;
      }

      if (doc.minAmount !== null) {
        this._minAmount.value = doc.minAmount;
      }

      if (doc.defaultExtWFlowAmount) {
        this._defaultExtWFlowAmount.value = doc.defaultExtWFlowAmount
      }
    }
  }

  @autobind
  public setAgent(agent: ModelAgent | null) {
    this._agent.value = agent;
  }

  public reset() {
    this._agent.value = null;
    // this._selectedPaymentSystem.value = null;
    this.agentsAmountRanges.value = null;
  }

  public async fetchAgentsAmountRanges() {
    try {
      this.isAgentsRangesLoading.value = true;
      const modelAgentsAmountRanges = await this._purchaseRepository.getAgentsAmountRanges();

      this.setAgentsAmountRanges(modelAgentsAmountRanges);

      return this.agentsAmountRanges.value;
    } catch (e: any) {
      LogService.of().error("NewPaymentController@loadPaymentSystems", e.message);
      await ErrorsService.of().handle(e);
    } finally {
      setTimeout(() => {
        this.isAgentsRangesLoading.value = false;
      }, 314);
    }
  }

  public setAgentsAmountRanges(ranges: AgentsAmountRanges) {
    if (this._minAmountEnabled.value) {
      this.agentsAmountRanges.value = this.calculateMinMaxAmountRangesByFirebase(ranges);
    } else {
      this.agentsAmountRanges.value = ranges;
    }
  }

  private calculateMinMaxAmountRangesByFirebase(agentsAmountRanges: AgentsAmountRanges) {
    const {buy} = this._account.value!.currency.rates;
    let minAmount = this._minAmount.value * buy;

    if (minAmount > agentsAmountRanges.min!) {
      agentsAmountRanges.min = Math.min(minAmount, agentsAmountRanges.max!);
    }

    return agentsAmountRanges;
  }

  // private initSelectedPaymentSystem() {
  //     const defaultBank = humps(DEFAULT_PAYMENT_SYSTEM);
  //     if (!this.paymentSystems.value || this.paymentSystems.value.length === 0) return defaultBank;
  //     const bank = this.paymentSystems.value.find(el => el.sysName === "anybank");
  //     return typeof bank !== "undefined"
  //         ? bank
  //         : defaultBank;
  // }

  public calculateStartAmountValue(): number {
    this.setPurchaseFirebaseConfig();

    if (this._flow.value === CreatePaymentFlow.classic) {
      const {buy} = this._account.value!.currency.rates;

      if (this.agentsAmountRanges.value == null) {
        return Math.ceil(this._defaultAmount.value * buy);
      } else {
        return Math.ceil(this.agentsAmountRanges.value!.min!);
      }
    } else {
      return this._defaultExtWFlowAmount.value;
    }
  }

  public validateMinmax() {
    let amount = this.amount.value;
    const { min, max } = this.minMaxRangesAmountByFlow.value;

    if (this._flow.value === CreatePaymentFlow.classic) {
      if (!min || !max) return true;

      return !(amount < min || amount > max);
    } else {
      if (
        this.canUseExtWFlow() &&
        this._account.value &&
        this._account.value.wallet
      ) {
        return !(amount < min || amount > max);
      } else {
        return false;
      }
    }
  }

  /**
   * Main method for create payment
   */
  @autobind
  async next() {
    if (this._flow.value === CreatePaymentFlow.classic) {
      return await this.makeClassicPayment(this.amount.value);
    } else {
      return await this.makeFromBromoneyPayment(this.amount.value);
    }
  }

  private makeFromBromoneyPayment(amount: number) {
    const payload: createPaymentPayload = {
      amount: amount,
    }
    return this._paymentService.createFromBromoneyBalancePayment(payload);
  }

  private async makeClassicPayment(amount: number) {
    const payload: createPaymentPayload = {
      amount: amount,
      psystem_id: null,
    }

    if (this._isCanSelectAgent && this._agent.value) {
      payload.account_ids = [this._agent.value!.id];
    }

    let operation;

    //TODO Скорее всего это условие уже устарело, после переработки flow в light платеже
    if (this._paymentService.payment.value?.subtype === "lite") {
      payload.msid = this._paymentService.payment.value!.msid!;
      operation = await this._purchaseRepository.patchEmptyPayment(payload)
    } else {
      operation = await this._paymentService.createPayment(payload);
    }

    if (operation == null) throw new Error("Something went wrong");

    FirebaseService.of().wrongRangeMetricSuccess.checkAndSendAnalyticEvent();
    FirebaseService.of().emptyRangeMetricSuccess.checkAndSendAnalyticEvent();

    return operation;
  }

  public setMaxAmount = () => {
    if (this.flow.value === CreatePaymentFlow.classic) {
      const { min, max } = this.minMaxRangesAmountByFlow.value;
      if (!min || !max) return false;

      this.amount.value = Math.floor(max);
    } else {
      if (
        this.canUseExtWFlow() &&
        this._account.value &&
        this._account.value.wallet &&
        this._account.value.wallet.balance.currency
      ) {
        this.amount.value = this._account.value.wallet.balance.amount;
      }
    }
  }

  destructor() {
    this.scope.stop();
    this.reset();
    NewPaymentController.instance = undefined;
  }
}
